import React, { useState, useEffect, useContext, useCallback } from "react";
import Box from "@mui/material/Box";
import MDBox from "components/MDBox";
import Card from "@mui/material/Card";
import Backdrop from "@mui/material/Backdrop";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import LogoutIcon from "@mui/icons-material/Logout";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { AuthContext } from "context";
import AuthService from "services/auth-service";
import WorkspaceService from "services/workspace-service";
import UserService from "services/user-service";
import { useNavigate } from "react-router-dom";
import MDTypography from "components/MDTypography";
import { debounce } from "lodash";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";

import WelcomeMessage from "./WelcomeMessage";

function CreateWorkspaceModal({ open, onClose, user }) { // [MODIFIED] Added onClose
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [isEditingSlug, setIsEditingSlug] = useState(false);
    const [isSlugLoading, setIsSlugLoading] = useState(false);
    const [isSlugAvailable, setIsSlugAvailable] = useState(true);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const authContext = useContext(AuthContext);
    const navigate = useNavigate();

    // Kebab menu state
    const [kebabAnchor, setKebabAnchor] = useState(null);
    const handleKebabOpen = (e) => setKebabAnchor(e.currentTarget);
    const handleKebabClose = () => setKebabAnchor(null);

    // Workspace switcher state
    const [workspaceMenuAnchor, setWorkspaceMenuAnchor] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);
    const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);

    const handleWorkspaceMenuOpen = async (e) => {
        setWorkspaceMenuAnchor(e.currentTarget);
        setLoadingWorkspaces(true);
        try {
            const response = await UserService.fetchUserWorkspaces();
            const list = Array.isArray(response) ? response : (response.data || []);
            setWorkspaces(list);
        } catch (err) {
            console.error("Failed to fetch workspaces", err);
        } finally {
            setLoadingWorkspaces(false);
        }
    };
    const handleWorkspaceMenuClose = () => setWorkspaceMenuAnchor(null);

    const handleSwitchWorkspace = async (workspaceId) => {
        try {
            await UserService.switchActiveWorkspace(workspaceId);
            localStorage.setItem("activeWorkspaceId", workspaceId);
            window.dispatchEvent(new CustomEvent("profile-updated"));
            handleWorkspaceMenuClose();
            handleKebabClose();
            if (!onClose) {
                // Hard reload to ensure all data is correctly fetched under the new workspace
                window.location.href = "/dashboard";
            }
        } catch (err) {
            console.error("Failed to switch workspace", err);
        }
    };

    // Fetch initial random slug on mount/open
    useEffect(() => {
        if (open) {
            const fetchInitialSlug = async () => {
                setIsSlugLoading(true);
                try {
                    const data = await WorkspaceService.previewSlug(""); // Empty string for random
                    setSlug(data.slug);
                    setIsSlugAvailable(true);
                } catch (error) {
                    console.error("Error fetching initial slug:", error);
                } finally {
                    setIsSlugLoading(false);
                }
            };
            fetchInitialSlug();
        }
    }, [open]);

    // Validate manually entered slug
    const validateSlug = useCallback(
        debounce(async (val) => {
            if (!val) return;
            setIsSlugLoading(true);
            try {
                const data = await WorkspaceService.validateSlug(val);
                setIsSlugAvailable(data.available);
            } catch (error) {
                console.error("Error validating slug:", error);
            } finally {
                setIsSlugLoading(false);
            }
        }, 500),
        []
    );

    const handleNameChange = (e) => {
        setName(e.target.value);
        setErrorMessage("");
    };

    const handleSlugChange = (e) => {
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
        validateSlug(val);
    };

    const handleRefreshSlug = async () => {
        setIsSlugLoading(true);
        try {
            const data = await WorkspaceService.previewSlug(""); // Generate new random
            setSlug(data.slug);
            setIsSlugAvailable(true);
            setIsEditingSlug(false); // Auto-exit edit mode as per requirement
        } catch (error) {
            console.error("Error refreshing slug:", error);
        } finally {
            setIsSlugLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!name.trim()) {
            setErrorMessage("Workspace name is required.");
            return;
        }
        if (!slug.trim()) {
            setErrorMessage("Workspace ID (slug) is required.");
            return;
        }
        if (!isSlugAvailable) {
            setErrorMessage("This Workspace ID is already taken.");
            return;
        }

        setLoading(true);
        setErrorMessage("");
        try {
            await WorkspaceService.createWorkspace(name, slug);
            // [NEW] Flag for first-time onboarding invite step — only in forced mode
            if (!onClose) {
                sessionStorage.setItem("firstWorkspaceSetup", "true");
                sessionStorage.setItem("firstWorkspaceName", name.trim());
            }
            // [MODIFIED] Logic for redirection
            window.location.href = "/profile?tab=subscription";
        } catch (error) {
            console.error("Error creating workspace:", error);
            setErrorMessage(error.message || "Failed to create workspace.");
        } finally {
            setLoading(false);
        }
    };

    const handleLogOut = async () => {
        await AuthService.logout();
        authContext.logout();
        navigate("/authentication/sign-in");
    };

    const renderContent = () => (
        <Card
            sx={{
                width: onClose ? "100%" : "90%", // Full width in Dialog, 90% in Backdrop
                maxWidth: 550,
                p: 4,
                backgroundColor: "white",
                borderRadius: "16px",
                position: "relative",
                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                mx: "auto"
            }}
        >
            {/* 3-dot kebab menu — top right */}
            <MDBox sx={{ position: "absolute", top: 16, right: 16 }}>
                <IconButton
                    size="small"
                    onClick={handleKebabOpen}
                    sx={{ color: "text.secondary" }}
                >
                    <MoreVertIcon />
                </IconButton>
                <Menu
                    anchorEl={kebabAnchor}
                    open={Boolean(kebabAnchor)}
                    onClose={handleKebabClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{ mt: 0.5, zIndex: 9999 }}
                >
                    <MenuItem onClick={handleWorkspaceMenuOpen}>
                        <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                        Switch Workspace
                    </MenuItem>
                    <Menu
                        anchorEl={workspaceMenuAnchor}
                        open={Boolean(workspaceMenuAnchor)}
                        onClose={handleWorkspaceMenuClose}
                        anchorOrigin={{ vertical: "top", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        sx={{ zIndex: 10000 }}
                    >
                        {loadingWorkspaces ? (
                            <MenuItem disabled><CircularProgress size={16} sx={{ mr: 1 }} /> Loading...</MenuItem>
                        ) : workspaces.length > 0 ? (
                            workspaces.map((ws) => (
                                <MenuItem
                                    key={ws.workspace_id?._id || ws._id}
                                    onClick={() => handleSwitchWorkspace(ws.workspace_id?._id || ws._id)}
                                    selected={ws.is_active}
                                >
                                    {ws.workspace_id?.name || "Unknown"} {ws.is_active && "(Active)"}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem disabled>No other workspaces</MenuItem>
                        )}
                    </Menu>
                    <MenuItem onClick={() => { handleKebabClose(); handleLogOut(); }}>
                        <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                        Log Out
                    </MenuItem>
                </Menu>
            </MDBox>

            <MDTypography variant="h5" fontWeight="bold" textAlign="center" mb={2} color="info">
                Create Your Workspace
            </MDTypography>

            {/* [MODIFIED] Only show welcome message if forced (no onClose) */}
            {!onClose && <WelcomeMessage userName={user?.name} />}

            {errorMessage && (
                <MDBox mb={2}>
                    <Typography variant="caption" color="error">
                        {errorMessage}
                    </Typography>
                </MDBox>
            )}

            <MDBox mb={3}>
                <MDInput
                    type="text"
                    label="Workspace Name"
                    fullWidth
                    value={name}
                    onChange={handleNameChange}
                    placeholder="e.g. My Agency"
                />
            </MDBox>

            <MDBox mb={1} display="flex" alignItems="center">
                {isEditingSlug ? (
                    <MDInput
                        type="text"
                        label="Workspace ID"
                        fullWidth
                        value={slug}
                        onChange={handleSlugChange}
                        error={!isSlugAvailable}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleRefreshSlug}
                                        disabled={isSlugLoading}
                                        size="small"
                                        sx={{ mr: !isSlugAvailable ? 2.5 : 0 }}
                                    >
                                        {isSlugLoading ? (
                                            <CircularProgress size={20} color="inherit" />
                                        ) : (
                                            <RefreshIcon />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                ) : (
                    <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                        sx={{ borderBottom: "1px solid #ddd", pb: 0.5, minHeight: "40px" }}
                    >
                        <MDTypography variant="caption" color="text" sx={{ opacity: 0.8, fontSize: "0.75rem" }}>
                            Workspace ID: <span style={{ fontWeight: "bold", color: "#1A73E8" }}>{isSlugLoading ? "..." : (slug || "...")}</span>
                        </MDTypography>
                        <MDButton
                            size="small"
                            color="info"
                            variant="text"
                            onClick={() => setIsEditingSlug(true)}
                            sx={{ p: 0, minWidth: 0, ml: 1, textTransform: "none" }}
                        >
                            <MDTypography variant="caption" color="info" fontWeight="bold">
                                Edit
                            </MDTypography>
                        </MDButton>
                    </MDBox>
                )}
            </MDBox>

            <MDBox mb={3} px={1}>
                <MDTypography variant="caption" color={isSlugAvailable ? "text" : "error"} display="block" sx={{ fontSize: "0.7rem", opacity: 0.7 }}>
                    {!isSlugAvailable
                        ? "This Workspace ID is already taken."
                        : "Workspace ID is unique. It cannot be changed later."}
                </MDTypography>
            </MDBox>

            <MDBox display="flex" justifyContent="center">
                <MDButton
                    variant="contained"
                    color="accent"
                    fullWidth
                    onClick={handleCreate}
                    disabled={loading || !isSlugAvailable || isSlugLoading || !name.trim() || !slug.trim()}
                >
                    {loading ? "Creating..." : "Create Workspace"}
                </MDButton>
            </MDBox>

            {/* Optional Close button inside the card for Dialog mode if we want it, or rely on Dialog actions. 
                But the design has been card-based. 
             */}
        </Card>
    );

    // If onClose is provided, use Dialog (Optional Mode)
    if (onClose) {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: { backgroundColor: "transparent", boxShadow: "none" }
                }}
            >
                {renderContent()}
            </Dialog>
        );
    }

    // Default: Forced Mode (Backdrop)
    return (
        <Backdrop
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.modal + 1,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
            }}
        >
            <Slide direction="up" in={open} mountOnEnter unmountOnExit>
                {renderContent()}
            </Slide>
        </Backdrop>
    );
}

export default CreateWorkspaceModal;
